import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { mangaArticles } from '@/mocks/mangaArticles';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [displayCount, setDisplayCount] = useState(8);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeCategory]);

  const filteredManga = activeCategory === 'all'
    ? mangaArticles
    : activeCategory === 'hot'
      ? [...mangaArticles].sort((a, b) => b.rating - a.rating)
      : activeCategory === 'new'
        ? [...mangaArticles].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime())
        : mangaArticles.filter((m) => m.category === activeCategory);

  const displayedManga = filteredManga.slice(0, displayCount);

  const handleCategoryChange = (slug: string) => {
    setActiveCategory(slug);
    setDisplayCount(8);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead
        title="漫画分类 - 禁漫天堂 | 热血·恋爱·奇幻·悬疑全品类漫画"
        description="禁漫天堂漫画分类页面，涵盖热血少年、恋爱甜宠、奇幻冒险、动作格斗、搞笑日常、悬疑推理、科幻未来等全品类正版漫画，快速找到你喜爱的作品。"
        keywords="禁漫天堂漫画分类,热血漫画,恋爱漫画,奇幻漫画,悬疑漫画,科幻漫画"
        canonicalUrl="https://jmtt.org/category"
        schemaJsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: '漫画分类 - 禁漫天堂',
            url: 'https://jmtt.org/category',
            description: '禁漫天堂全品类漫画分类页面',
            isPartOf: {
              '@type': 'WebSite',
              name: '禁漫天堂',
              url: 'https://jmtt.org/',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://jmtt.org/' },
              { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://jmtt.org/category' },
            ],
          },
        ]}
      />

      <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '漫画分类' }]} />

      <main className="w-full pb-16 md:pb-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
              漫画分类
            </h1>
            <p className="text-sm text-foreground-500">探索你喜欢的漫画类型，发现精彩作品</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50 dark:text-foreground-950 shadow-sm'
                    : 'bg-background-100 text-foreground-700 hover:bg-secondary-100'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {filteredManga.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-book-2-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无作品，敬请期待</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {displayedManga.map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/article/${manga.id}`}
                    className="group bg-background-100 rounded-xl overflow-hidden hover:bg-background-200/70 transition-colors duration-200"
                  >
                    <div className="relative overflow-hidden aspect-[3/4]">
                      <img
                        src={manga.coverImage}
                        alt={manga.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {manga.rating}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <span className="text-xs text-background-50 font-medium">{manga.status}</span>
                      </div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                        {manga.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1 line-clamp-2 leading-relaxed">
                        {manga.description}
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <span className="text-xs text-foreground-400">{manga.author}</span>
                        <span className="text-xs text-foreground-400">{manga.views.toLocaleString()} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {displayedManga.length < filteredManga.length && (
                <div className="text-center mt-10">
                  <button
                    onClick={() => setDisplayCount((prev) => prev + 8)}
                    className="bg-background-100 text-foreground-700 px-8 py-3 rounded-full text-sm font-medium whitespace-nowrap hover:bg-secondary-100 transition-colors cursor-pointer"
                  >
                    加载更多作品
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
}